// Intercepts the cart's checkout submit when the cart contains one or more configured-safe
// lines (added via the draft-order beta path in extensions/theme-extension/blocks/
// product-configurator.liquid — marked with the _configurator: 'true' line property) and
// routes checkout through a server-authoritative Shopify Draft Order instead of Shopify's
// native checkout. Carts with no configured lines are untouched — the native form submits
// exactly as before.
(function () {
  function getCart() {
    return fetch('/cart.js').then(function (res) {
      return res.json();
    });
  }

  function getConfiguredLines(cart) {
    return (cart.items || []).filter(function (item) {
      return item.properties && item.properties._configurator === 'true';
    });
  }

  function createDraftOrderCheckout(configuredLines, cart, acknowledgePriceChange) {
    const selections = configuredLines.map(function (item) {
      return {
        selectionId: item.properties._configurationId,
        sessionToken: item.properties._sessionToken,
      };
    });

    return fetch('/apps/meta-variant-builder-api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `
          mutation CreateCheckoutDraftOrder($selections: String!, $cart: String!, $acknowledgePriceChange: Boolean) {
            createCheckoutDraftOrder(
              selections: $selections
              cart: $cart
              acknowledgePriceChange: $acknowledgePriceChange
            ) {
              success
              errors { message code }
              result
            }
          }
        `,
        variables: {
          selections: JSON.stringify(selections),
          cart: JSON.stringify(cart),
          acknowledgePriceChange: !!acknowledgePriceChange,
        },
      }),
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Checkout API error: ' + response.status);
        }
        return response.json();
      })
      .then(function (result) {
        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message || 'Failed to start checkout');
        }
        const data = result.data && result.data.createCheckoutDraftOrder;
        if (!data || !data.success) {
          const msg = (data && data.errors && data.errors[0] && data.errors[0].message) || 'Failed to start checkout';
          throw new Error(msg);
        }
        return data.result;
      });
  }

  function handleCheckoutResult(configuredLines, cart, checkoutResult, submitter, originalText) {
    if (!checkoutResult.priceChanged) {
      window.location.href = checkoutResult.invoiceUrl;
      return;
    }

    const changes = checkoutResult.changes || [];
    const summary = changes
      .map(function (c) {
        return 'Price changed from $' + c.oldPrice + ' to $' + c.newPrice;
      })
      .join('\n');
    const confirmed = window.confirm(
      (summary || 'One or more configured items changed price since you added them to your cart.') +
        '\n\nContinue to checkout at the new price?'
    );
    if (!confirmed) {
      submitter.disabled = false;
      submitter.textContent = originalText;
      return;
    }

    createDraftOrderCheckout(configuredLines, cart, true)
      .then(function (r) {
        window.location.href = r.invoiceUrl;
      })
      .catch(function (error) {
        console.error('Draft order checkout (acknowledged) failed:', error);
        alert(error.message || 'Failed to start checkout. Please try again.');
        submitter.disabled = false;
        submitter.textContent = originalText;
      });
  }

  // Re-submits the form exactly as the browser would have for a real click on
  // `submitter` (preserving its name=value pair, e.g. checkout=Checkout) — unlike
  // plain form.submit(), which drops ALL submit-button data and made Shopify treat
  // the resubmission as a plain cart update, reloading /cart instead of proceeding
  // to /checkout. requestSubmit() re-fires the 'submit' event, so a bypass flag
  // keeps this same listener from intercepting its own resubmission a second time.
  function nativeSubmit(form, submitter) {
    form.dataset.mvbCheckoutBypass = 'true';
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = submitter.name;
      hidden.value = submitter.value;
      form.appendChild(hidden);
      form.submit();
    }
  }

  // Shop Pay / Google Pay / Apple Pay ("accelerated checkout") buttons go straight to Shopify's native
  // checkout, which would skip the draft order this script exists to create. The cart template already
  // omits them when it renders with a configured line; this covers carts that change without a full page
  // load (mini-cart, AJAX updates) by hiding them whenever the live cart holds a configured line.
  var ACCEL_STYLE_ID = 'mvb-hide-accelerated-checkout';
  function setAcceleratedCheckoutHidden(hide) {
    var existing = document.getElementById(ACCEL_STYLE_ID);
    if (!hide) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;
    var style = document.createElement('style');
    style.id = ACCEL_STYLE_ID;
    style.textContent =
      '.additional-checkout-buttons, shopify-accelerated-checkout-cart, shopify-accelerated-checkout, ' +
      '[data-shopify="payment-button"], .shopify-payment-button, .dynamic-checkout__content { display: none !important; }';
    document.head.appendChild(style);
  }
  function syncAcceleratedCheckout() {
    getCart()
      .then(function (cart) {
        setAcceleratedCheckoutHidden(getConfiguredLines(cart).length > 0);
      })
      .catch(function () {});
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAcceleratedCheckout);
  } else {
    syncAcceleratedCheckout();
  }
  ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (name) {
    document.addEventListener(name, syncAcceleratedCheckout);
  });

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (form.dataset.mvbCheckoutBypass === 'true') {
        delete form.dataset.mvbCheckoutBypass;
        return;   // our own re-submission from nativeSubmit() — let it proceed
      }

      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;

      // Only intercept real cart-checkout forms (posts to the cart URL), not unrelated forms
      // that might also happen to have a "checkout"-named submit button.
      if (!form.action || form.action.indexOf('/cart') === -1) return;

      event.preventDefault();

      getCart()
        .then(function (cart) {
          const configuredLines = getConfiguredLines(cart);
          if (configuredLines.length === 0) {
            // Nothing configured in the cart — let native checkout proceed unmodified.
            nativeSubmit(form, submitter);
            return;
          }

          const originalText = submitter.textContent;
          submitter.disabled = true;
          submitter.textContent = 'Preparing checkout...';

          createDraftOrderCheckout(configuredLines, cart, false)
            .then(function (checkoutResult) {
              handleCheckoutResult(configuredLines, cart, checkoutResult, submitter, originalText);
            })
            .catch(function (error) {
              console.error('Draft order checkout failed:', error);
              alert(error.message || 'Failed to start checkout. Please try again.');
              submitter.disabled = false;
              submitter.textContent = originalText;
            });
        })
        .catch(function (error) {
          console.error('Failed to load cart for checkout interception:', error);
          nativeSubmit(form, submitter);
        });
    },
    true
  );
})();
